import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { z } from 'zod';

import { formatCivilDate, formatInstant } from '@/shared/format/date';
import { formatCents } from '@/shared/format/money';
import { defineBlock, type BlockViewProps } from '@/shared/page';
import { DescriptionList, type DescriptionItem } from '@/shared/record';
import { action } from '@/shared/screen';
import { Button } from '@/shared/ui/button';

import { bankSlipDetailSource, bankSlipParams } from './bank-slip.sources';
import { bankSlipInstantDate, bankSlipPersonLabel } from './bank-slip.presentation';
import { bankSlipStatus } from './bank-slip.enums';

export const bankSlipCopy = action({
  id: 'bankSlip.copy',
  kind: 'read',
  label: msg`Copiar`,
  description: 'Copies one bank slip payment identifier on the authorized host.',
  input: z.object({ field: z.enum(['barCode', 'numericLine']) }),
  run: () => undefined,
});
export const bankSlipReceipt = action({
  id: 'bankSlip.receipt',
  kind: 'read',
  label: msg`Download recibo`,
  description: 'Downloads a receipt for a bank slip confirmation transaction.',
  input: z.object({ id: z.string() }),
  run: () => undefined,
});
export const bankSlipBeneficiary = action({
  id: 'bankSlip.openBeneficiary',
  kind: 'navigate',
  label: msg`Beneficiário`,
  description: 'Opens the person list filtered by the beneficiary document, in the same tab, through the billing host.',
  run: () => undefined,
});
type View = BlockViewProps<
  z.output<typeof bankSlipParams>,
  { detail: typeof bankSlipDetailSource }
>;

function useDetailItems({ data, params, run }: View): DescriptionItem[] {
  const { t, i18n } = useLingui();
  const b = data.detail.bankSlip;
  const missing = t`N/D`;
  const date = (value: string | null | undefined) =>
    value ? (formatCivilDate(value, { locale: i18n.locale }) ?? missing) : missing;
  const money = (value: number | null | undefined) =>
    value == null ? missing : formatCents(value, { locale: i18n.locale });
  const person = (value: typeof b.beneficiary) => bankSlipPersonLabel(value, missing);
  const fee = (value: number | null | undefined, type: string | null | undefined) =>
    value == null
      ? missing
      : type?.toLowerCase() === 'percent'
        ? new Intl.NumberFormat(i18n.locale, { style: 'percent', maximumFractionDigits: 4 }).format(
            value,
          )
        : money(value);
  const rows: (DescriptionItem | false)[] = [
    b.walletCode !== b.walletCodeOrigem && {
      id: 'origin',
      label: t`Código convênio de origem`,
      value: String(b.walletCodeOrigem),
    },
    {
      id: 'ourNumber',
      label: t`Nosso número`,
      value: `${b.ourNumber ?? missing}-${b.ourNumberDigit ?? missing}`,
    },
    { id: 'amount', label: t`Valor do boleto`, value: money(b.amount) },
    {
      id: 'created',
      label: t`Data de registro do boleto`,
      value: b.createdAt
        ? formatInstant(b.createdAt, { locale: i18n.locale, time: false })
        : missing,
    },
    { id: 'due', label: t`Data de vencimento`, value: date(b.dueDate) },
    {
      id: 'beneficiary',
      label: t`Beneficiário`,
      value: b.beneficiary?.registrationNumber ? (
        <button
          type="button"
          className="cursor-pointer text-left text-primary underline underline-offset-2"
          onClick={() => run('bankSlip.openBeneficiary')}
        >
          {person(b.beneficiary)}
        </button>
      ) : (
        person(b.beneficiary)
      ),
    },
    { id: 'guarantor', label: t`Sacador avalista`, value: person(b.guarantor) },
    { id: 'issuance', label: t`Data emissão do título`, value: date(b.issuanceDate) },
    b.settlementAt != null && {
      id: 'settled',
      label: t`Data de liquidação`,
      value: bankSlipInstantDate(b.settlementAt, i18n.locale) ?? missing,
    },
    { id: 'limit', label: t`Data limite pagamento`, value: date(b.overdueLimitDate) },
    { id: 'discountLimit', label: t`Data limite desconto`, value: date(b.discountLimitDate) },
    { id: 'deduction', label: t`Valor do abatimento`, value: money(b.deduction) },
    { id: 'fine', label: t`Multa`, value: fee(b.fineAmount, b.fineTypeValue) },
    { id: 'interest', label: t`Juros de mora`, value: fee(b.interestAmount, b.interestTypeValue) },
    { id: 'discount', label: t`Descontos`, value: money(b.discount) },
    params.isRoot && {
      id: 'protest',
      label: t`Situação protesto`,
      value: bankSlipStatus.label(
        i18n,
        b.statusDebtCollectionValue.toLowerCase(),
        b.statusDebtCollectionValueDisplay,
      ),
    },
    params.isRoot && {
      id: 'settlement',
      label: t`Situação liquidação`,
      value: bankSlipStatus.label(
        i18n,
        b.statusSettlementValue.toLowerCase(),
        b.statusSettlementValueDisplay,
      ),
    },
    params.isRoot && {
      id: 'registration',
      label: t`Situação registro`,
      value: bankSlipStatus.label(
        i18n,
        b.statusRegisterValue.toLowerCase(),
        b.statusRegisterValueDisplay,
      ),
    },
    { id: 'cancel', label: t`Motivo de baixa`, value: b.cancelReason ?? missing },
    { id: 'description', label: t`Descrição do boleto`, value: b.description ?? missing },
    !!b.paymentMethodDisplay && {
      id: 'payment',
      label: t`Tipo de pagamento`,
      value: b.paymentMethodDisplay,
    },
    !!b.captureTypeDisplay && {
      id: 'capture',
      label: t`Tipo de captura`,
      value: b.captureTypeDisplay,
    },
  ];
  return rows.filter((row): row is DescriptionItem => row !== false);
}
function DetailsWeb(view: View) {
  return <DescriptionList columns={3} items={useDetailItems(view)} />;
}
function DetailsMobile(view: View) {
  return (
    <dl className="grid grid-cols-2 gap-4">
      {useDetailItems(view).map((item) => (
        <div key={item.id} className="min-w-0">
          <dt className="text-xs text-muted-foreground">{item.label}</dt>
          <dd className="text-sm font-medium break-words">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}
function Codes({ data, run, mobile }: View & { mobile: boolean }) {
  const { t } = useLingui();
  return (
    <div className={mobile ? 'grid gap-4' : 'grid grid-cols-2 gap-6'}>
      {(['barCode', 'numericLine'] as const).map((field) => (
        <div key={field} className="min-w-0">
          <p className="text-sm text-muted-foreground">
            {field === 'barCode' ? t`Código de barras` : t`Linha digitável`}
          </p>
          <p className="font-mono text-sm break-all">{data.detail.bankSlip[field]}</p>
          <Button variant="secondary" onClick={() => run('bankSlip.copy', { field })}>
            {field === 'barCode' ? t`Copiar código de barras` : t`Copiar linha digitável`}
          </Button>
        </div>
      ))}
    </div>
  );
}
function CodesWeb(view: View) {
  return <Codes {...view} mobile={false} />;
}
function CodesMobile(view: View) {
  return <Codes {...view} mobile />;
}
function History({ data, run, mobile }: View & { mobile: boolean }) {
  const { t, i18n } = useLingui();
  const b = data.detail.bankSlip;
  const lines = [...b.fileInstructionLines, ...b.fileConfirmationLines].sort((a, c) =>
    String(a.detail.createdAt).localeCompare(String(c.detail.createdAt)),
  );
  return (
    <ol className={mobile ? 'space-y-3' : 'space-y-4 border-l border-border pl-5'}>
      {lines.map(({ id, lineType, detail: d }) => {
        const returned = lineType === 'RetornoTransacao';
        const fields = returned
          ? [
              [
                t`Vencimento título`,
                d.dueDate ? formatCivilDate(d.dueDate, { locale: i18n.locale }) : null,
              ],
              [t`Motivo`, d.occurrenceReason],
              [
                t`Data da ocorrência`,
                d.paidCreditDay ? bankSlipInstantDate(d.paidCreditDay, i18n.locale) : null,
              ],
              [
                t`Motivo da baixa`,
                d.confirmationTransactionTypeValueDisplay?.toLowerCase() === 'baixado'
                  ? `${d.occurrenceCode ?? ''} - ${d.occurrenceReason ?? ''}`
                  : null,
              ],
              [t`Número do protocolo`, d.debtCollectionProtocolNumber],
              [t`Nome do cartório`, d.debtCollectionName],
              [
                t`Endereço do cartório`,
                d.debtCollectionCity && d.debtCollectionState
                  ? `${d.debtCollectionCity}-${d.debtCollectionState}`
                  : null,
              ],
              [t`Cartório`, d.debtCollectionRegistryNumber],
              [t`Custo de tarifa`, d.costs],
            ]
          : [
              [t`Situação`, d.statusOperationValueDisplay],
              [t`Mensagem`, d.error],
            ];
        fields.push(
          [
            t`Data de processamento`,
            d.createdAt ? formatInstant(d.createdAt, { locale: i18n.locale }) : null,
          ],
          [t`Criado por`, d.createdBy?.userIdDisplay],
          [t`Atualizado por`, d.updatedBy?.userIdDisplay],
        );
        return (
          <li key={id} className="rounded-lg border border-border p-4">
            <h3 className="font-semibold">{d.confirmationTransactionTypeValueDisplay}</h3>
            <dl className="grid gap-2">
              {fields
                .filter(([, value]) => value)
                .map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-xs text-muted-foreground">{label}</dt>
                    <dd className="text-sm break-words">{value}</dd>
                  </div>
                ))}
            </dl>
            {returned && (
              <Button
                variant="secondary"
                onClick={() => run('bankSlip.receipt', { id })}
              >{t`Download recibo`}</Button>
            )}
          </li>
        );
      })}
    </ol>
  );
}
function HistoryWeb(view: View) {
  return <History {...view} mobile={false} />;
}
function HistoryMobile(view: View) {
  return <History {...view} mobile />;
}
const common = {
  status: 'screen' as const,
  params: bankSlipParams,
  sources: { detail: bankSlipDetailSource },
  query: ({ id }: z.output<typeof bankSlipParams>) => ({ detail: { id } }),
  skeleton: {
    web: { kind: 'record' as const, rows: 6 },
    mobile: { kind: 'record' as const, rows: 8 },
  },
};
export const bankSlipDetailsBlock = defineBlock({
  ...common,
  id: 'bankSlip.details',
  actions: [bankSlipBeneficiary],
  isEmpty: ({ detail }) => detail.bankSlip.id === '',
  title: msg`Dados do boleto`,
  description: 'Bank slip amounts, dates, beneficiary, instructions and settlement status.',
  empty: { title: msg`Boleto não encontrado` },
  render: { web: DetailsWeb, mobile: DetailsMobile },
});
export const bankSlipCodesBlock = defineBlock({
  ...common,
  id: 'bankSlip.codes',
  isEmpty: ({ detail }) => detail.bankSlip.id === '',
  title: msg`Linha digitável e código de barras`,
  description: 'Payment identifiers with explicit clipboard actions on the authorized host.',
  actions: [bankSlipCopy],
  empty: { title: msg`Boleto não encontrado` },
  render: { web: CodesWeb, mobile: CodesMobile },
});
export const bankSlipHistoryBlock = defineBlock({
  ...common,
  id: 'bankSlip.history',
  title: msg`Histórico de alterações do boleto`,
  description:
    'Chronological bank slip instructions and confirmation lines with receipt downloads.',
  actions: [bankSlipReceipt],
  isEmpty: ({ detail }) =>
    detail.bankSlip.fileInstructionLines.length + detail.bankSlip.fileConfirmationLines.length ===
    0,
  empty: { title: msg`Nenhuma alteração do boleto` },
  render: { web: HistoryWeb, mobile: HistoryMobile },
});
